import React, { useRef } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import styles from './Qualifications.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { QualificationQuery } from 'services/api/api-client.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import { UseTranslationResponse } from 'react-i18next';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import formStyles from '../../../components/form/form.module.scss';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import Accepted from '../../../assets/icons/status_accepted.svg';
import Failed from '../../../assets/icons/status_failed.svg';
import Warning from '../../../assets/icons/status_warning.svg';
import { Tip } from '../../../components/uikit/Tip.tsx';
import i18n from 'i18next';
import SortArrow from 'assets/icons/sort-arrow.svg?react';
import SortArrowHover from 'assets/icons/sort-arrow-hover.svg?react';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

type Form = {
  participant: string;
  training: string;
  isValid: boolean;
  isExpireSoon: boolean;
  isExpired: boolean;
};
const TitleTooltipBody = () => {
  return (
    <div className={styles.tooltipText}>
      {i18n.t('Page.Qualifications.tooltip')}
    </div>
  );
};

const pageSizes = [10, 20, 50];

export const Qualifications = () => {
  const i18n = useScopedTranslation('Page.Qualifications');

  const [params, setParams] = useQueryParams({
    participant: StringParam,
    training: StringParam,
    isValid: BooleanParam,
    isExpireSoon: BooleanParam,
    isExpired: BooleanParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'validTo'),
    desc: withDefault(BooleanParam, false),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
    },
    {
      defaultValues: {
        participant: params.participant ?? '',
        training: params.training ?? '',
        isExpired: params.isExpired ?? false,
        isExpireSoon: params.isExpireSoon ?? false,
        isValid: params.isValid ?? false,
      },
    },
  );

  const qualificationData = QualificationQuery.useGetQuery(
    {
      participant: params.participant?.trim() || undefined,
      description: params.training?.trim() || undefined,
      isExpired: params.isExpired || false,
      isValid: params.isValid || false,
      isExpireSoon: params.isExpireSoon || false,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const [isExpired, isValid, isExpireSoon, training, participant] = useWatch({
    control: form.control,
    name: ['isExpired', 'isValid', 'isExpireSoon', 'training', 'participant'],
  });

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const columnsConfig = getColumnsConfig(
    i18n,
    params.desc,
    changeSorting,
    params.sortBy,
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const numberOfRows = qualificationData?.data?.data.length ?? 0;

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: qualificationData.data?.totalCount ?? 0,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const resetFilters = () => {
    form.reset({
      participant: '',
      training: '',
      isExpired: false,
      isExpireSoon: false,
      isValid: false,
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShown =
    !!params.participant ||
    !!params.training ||
    !!params.isExpired ||
    !!params.isExpireSoon ||
    !!params.isValid;

  const errObj = qualificationData?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !numberOfRows &&
    !qualificationData.isLoading &&
    !qualificationData.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'qualifications-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          qualificationData.isLoading || qualificationData.isPlaceholderData
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.filterRowContainer}>
            <Input
              testId={'participant-input'}
              {...form.register('participant')}
              leftHelperText={i18n.t('participant')}
              className={styles.width320}
              endAdornment={
                <div>
                  {participant && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('participant', '')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <Input
              testId={'training-input'}
              {...form.register('training')}
              leftHelperText={i18n.t('training')}
              className={styles.width320}
              endAdornment={
                <div>
                  {training && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('training', '')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <div className={styles.greyText}>{i18n.t('status')}</div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={isValid}
                onChange={(e) => {
                  form.setValue('isValid', e.target.checked);
                }}
                actionText={i18n.t('valid')}
                testId={'valid-checkbox'}
              />
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={isExpireSoon}
                onChange={(e) => {
                  form.setValue('isExpireSoon', e.target.checked);
                }}
                actionText={i18n.t('expire_soon')}
                testId={'expire-soon-checkbox'}
              />
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={isExpired}
                onChange={(e) => {
                  form.setValue('isExpired', e.target.checked);
                }}
                actionText={i18n.t('expired')}
                testId={'expired-checkbox'}
              />
            </div>
            <Button
              type={'submit'}
              className={clsx(styles.actionButton)}
              title={i18n.t('apply')}
              testId={'filters-apply-button'}
            />
            {isResetButtonShown && (
              <NegativeActionText
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
                testId={'filters-reset-button'}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={qualificationData.data?.data}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: qualificationData.data?.data.length }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={`${x.dataField}.${index}`}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                      isSortingBlocked={index === columnsConfig.length - 1}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.Qualifications'>,
  isDesc: boolean,
  changeSorting: (name: string) => void,
  sortBy?: string | null,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'name',
    allowEditing: false,
    allowSorting: true,
    sortingMethod: (a, b) => {
      return Number(a > b);
    },
    caption: i18n.t('participant'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 200,
    calculateCellValue: (a) => a.participant,
  },
  {
    dataField: 'description',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('training'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 300,
    headerCellRender: (info) => {
      const isSorted = info.column.dataField === sortBy;

      return (
        <div className={styles.tooltipContainer}>
          <div
            className={clsx(isSorted && styles.black)}
            onClick={(e) => {
              e.stopPropagation();
              if (typeof info.column.dataField === 'string') {
                changeSorting(info.column.dataField);
              }
            }}
          >
            {info.column.caption}
          </div>
          <div
            className={styles.marginRight5}
            onClick={(e) => e.stopPropagation()}
          >
            <Tip
              tooltipBody={<TitleTooltipBody />}
              defaultIconClassname={styles.tooltipIcon}
              className={styles.tooltip}
            />
          </div>
          {isSorted ? (
            isDesc ? (
              <SortArrow />
            ) : (
              <SortArrow className={styles.rotate180} />
            )
          ) : (
            <div className={clsx(styles.sortIconContainer, styles.displayNone)}>
              <SortArrowHover className={clsx(styles.rotate180)} />
            </div>
          )}
        </div>
      );
    },
  },
  {
    dataField: 'trainingFrom',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('training_from'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 150,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'trainingTo',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('training_to'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 150,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'validTo',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('valid_to'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    width: 150,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: '',
    allowEditing: false,
    allowSorting: false,
    caption: i18n.t('status'),
    cssClass: clsx(formStyles.textCellPadding),
    width: 100,
    cellRender: (row) => {
      const inputDate = new Date(row.data.validTo);

      const today = new Date();

      const sixMonthsLater = new Date(today);
      sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);

      if (inputDate < today) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('expired')}
            customImage={Failed}
            testId={'expired-tooltip'}
          />
        );
      } else if (inputDate > sixMonthsLater) {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('valid')}
            customImage={Accepted}
            testId={'valid-tooltip'}
          />
        );
      } else {
        return (
          <Tip
            className={styles.unsetBackgroundColor}
            tooltipText={i18n.t('expire_soon')}
            customImage={Warning}
            testId={'expire-soon-tooltip'}
          />
        );
      }
    },
  },
];
